import { darken, fade } from '@material-ui/core/styles/colorManipulator';
import { TTheme } from './types';

const hexToRGBArray = (color: string) => {
  const cleanColor = color.replace('#', '');
  const newColor =
    cleanColor.length === 3
      ? cleanColor.charAt(0) +
        cleanColor.charAt(0) +
        cleanColor.charAt(1) +
        cleanColor.charAt(1) +
        cleanColor.charAt(2) +
        cleanColor.charAt(2)
      : cleanColor;
  const rgb = [];
  for (let i = 0; i <= 2; i++) rgb[i] = parseInt(newColor.substr(i * 2, 2), 16);
  return rgb;
};

// Luma color system, not Luma Health => https://en.wikipedia.org/wiki/Luma_%28video%29
const colorToLuma = (color: string | number[]) => {
  const rgb = typeof color === 'string' ? hexToRGBArray(color) : color;
  return 0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2];
};

const getContrastColor = (color: string | number[]) => (colorToLuma(color) >= 165 ? '#000' : '#fff');

export const getCustomStyles = ({ fontFamily, colors, fontSizes }: TTheme) => ({
  typography: {
    fontFamily: fontFamily.name,
    body1: {
      fontSize: fontSizes.body,
    },
    body1Next: {
      fontSize: fontSizes.body,
    },
    body2: {
      fontSize: fontSizes.small,
    },
    body2Next: {
      fontSize: fontSizes.small,
    },
    button: {
      fontSize: fontSizes.small,
    },
    buttonNext: {
      fontSize: fontSizes.small,
    },
  },
  custom: {
    fontSizes,
    palette: {
      ...colors,
      textColor: colors.primary,
      accent2Color: fade(colors.secondary, 0.4),
      colorSuccessHover: darken(colors.colorSuccess, 0.2),
      colorDangerHover: darken(colors.colorDanger, 0.2),
      colorInfo: fade(colors.primary, 0.6),
    },
  },
  palette: {
    background: {
      default: colors.colorSupportSnow,
    },
    primary: {
      main: colors.primary,
      contrastText: getContrastColor(colors.primary),
      dark: darken(colors.primary, 0.2),
    },
    secondary: {
      main: colors.secondary,
      contrastText: getContrastColor(colors.secondary),
      dark: darken(colors.secondary, 0.2),
    },
    tertiary: {
      main: colors.white,
    },
    action: {
      hover: colors.colorLightGrayishBlue,
    },
    error: {
      main: colors.colorDanger,
    },
    text: {
      primary: colors.primary,
      secondary: colors.colorSupportSteel,
    },
    common: {
      black: colors.black,
      white: colors.white,
    },
  },
  overrides: {
    MuiFormHelperText: {
      root: {
        color: colors.colorSupportSteel,
      },
    },
    MuiLinearProgress: {
      colorPrimary: {
        backgroundColor: colors.colorSupportSteel,
      },
      bar1Determinate: {
        backgroundColor: colors.colorSupportSlate,
      },
      bar1Indeterminate: {
        backgroundColor: colors.colorSupportSlate,
      },
      bar2Indeterminate: {
        backgroundColor: colors.colorSupportSlate,
      },
    },
    MuiListSubheader: {
      sticky: {
        backgroundColor: colors.white,
      },
    },
    MuiListItem: {
      button: {
        '&:active': {
          backgroundColor: colors.colorSupportPowder,
        },
        '&:focus': {
          backgroundColor: colors.colorSupportPowder,
        },
        '&:hover': {
          backgroundColor: colors.colorSupportPowder,
        },
      },
    },
    MuiMenuItem: {
      root: {
        '&:hover': {
          backgroundColor: colors.colorSupportPowder,
        },
        '&$selected': {
          backgroundColor: `${colors.colorLightBlue} !important`,
          fontWeight: 600,
        },
      },
    },
    MuiButtonGroup: {
      groupedContainedPrimary: {
        '&:not(:last-child)': {
          borderColor: colors.white,
        },
      },
    },
    MuiRadio: {
      colorPrimary: {
        color: colors.primary,
      },
    },
    MuiCheckbox: {
      colorPrimary: {
        color: colors.primary,
      },
    },
    MuiTypography: {
      colorPrimary: {
        color: colors.primary,
      },
      body2: {
        color: colors.primary,
      },
    },
    MuiInputBase: {
      root: {
        backgroundColor: `${colors.white} !important`,
      },
    },
    MuiPaper: {
      root: {
        backgroundColor: colors.white,
      },
    },
    MuiToolbar: {
      root: { backgroundColor: colors.white },
    },
    MuiInput: {
      root: {
        border: `1px solid ${colors.colorLightBlue}`,
        '&$focused': {
          borderColor: colors.colorSupportSteel,
          zIndex: 10,
          '& input::selection, textarea::selection': {
            color: colors.primary,
          },
        },
        '&$disabled': {
          color: `${colors.colorSupportSteel} !important`,
        },
      },
      input: {
        fontSize: fontSizes.bodyS,
        backgroundColor: colors.white,
        color: colors.primary,
        '&::placeholder': {
          color: colors.colorSupportSteel,
        },
      },
      error: {
        borderColor: colors.colorDanger,
      },
    },
    MuiInputLabel: {
      root: {
        fontSize: fontSizes.bodyG,
        color: colors.primary,
      },
    },
    MuiIconButton: {
      colorPrimary: {
        color: colors.colorSupportSlate,
      },
    },
    MuiFormControlLabel: {
      label: {
        color: colors.primary,
      },
    },
    MuiIcon: {
      colorPrimary: {
        color: colors.colorSupportSlate,
      },
      root: {
        fontSize: fontSizes.heading,
      },
      fontSizeSmall: {
        fontSize: fontSizes.bodyG,
      },
      fontSizeLarge: {
        fontSize: fontSizes.largeIcon,
      },
    },
    MuiSvgIcon: {
      colorPrimary: {
        color: colors.primary,
      },
      root: {
        fontSize: fontSizes.heading,
      },
      fontSizeSmall: {
        fontSize: fontSizes.bodyG,
      },
      fontSizeLarge: {
        fontSize: fontSizes.largeIcon,
      },
    },
    MuiButtonBase: {
      root: {
        fontFamily: fontFamily.name,
      },
    },
    MuiButton: {
      root: {
        borderRadius: 0,
        color: colors.primary,
      },
      contained: {
        backgroundColor: colors.white,
      },
      containedPrimary: {
        color: getContrastColor(colors.primary),
      },
      containedSecondary: {
        color: getContrastColor(colors.secondary),
      },
      text: {
        color: colors.colorSupportSteel,
      },
      label: {
        fontWeight: 500,
        textTransform: 'none',
      },
      outlined: {
        border: `1px solid ${colors.colorSupportSteel}`,
      },
      sizeSmall: {
        height: 30,
      },
      sizeLarge: {
        fontSize: fontSizes.body,
      },
    },
    MuiDialogActions: {
      root: {
        backgroundColor: colors.colorVeryLightGray,
      },
    },
    MuiFormLabel: {
      root: {
        '&$focused,&$error': {
          color: colors.primary,
        },
      },
      asterisk: {
        color: colors.colorDanger,
      },
    },
    LumaIconButton: {
      colorPrimary: {
        color: colors.primary,
      },
    },
    MuiChip: {
      root: {
        fontSize: fontSizes.small,
        backgroundColor: colors.colorVeryLightGray,
        color: colors.primary,
      },
    },
    MuiDialogTitle: {
      root: {
        backgroundColor: colors.primary,
        color: colors.white,
        '& > h6': {
          color: 'inherit',
        },
      },
    },
    // LumaComponents
    LumaHeader: {
      root: {
        backgroundColor: colors.white,
      },
    },
    LumaDialogTitle: {
      root: {
        background: colors.primary,
        color: colors.white,
        '& > h6': {
          color: 'inherit',
        },
      },
    },
    LumaDialogActions: {
      root: {
        backgroundColor: colors.colorVeryLightGray,
      },
    },
    LumaStepper: {
      root: {
        background: 'transparent',
      },
    },
  },
});
